import { ColDef, ValueFormatterParams } from '@ag-grid-community/core';
import ClientModel from '@/models/crm/client.model';

// Cliente com CNPJs agrupados no ERP (ex.: CRM interno): o painel do cliente pai junta contatos,
// conversas e processos de todos os CNPJs.
export abstract class ClientCnpjHelper {
  static readonly originColId = 'originCnpj';

  static hasGroupedCnpjs(client?: ClientModel | null): boolean {
    return !!client && (client.cnpjsAgrupados || []).some((cnpj) => !ClientCnpjHelper.isSame(cnpj, client.cnpjCpf));
  }

  // Coluna "CNPJ de origem": nasce oculta e só aparece para cliente com CNPJs agrupados.
  static getOriginColDef(headerName: string): ColDef {
    return {
      headerName,
      colId: ClientCnpjHelper.originColId,
      field: 'cnpj',
      minWidth: 160,
      hide: true,
      suppressColumnsToolPanel: true,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams): string => ClientCnpjHelper.format(params.value),
    };
  }

  static normalize(value?: string | null): string {
    return (value || '').replace(/[^0-9A-Za-z]/g, '');
  }

  static isSame(a?: string | null, b?: string | null): boolean {
    const normalized = ClientCnpjHelper.normalize(a);
    return !!normalized && normalized === ClientCnpjHelper.normalize(b);
  }

  static format(value?: string | null): string {
    const cnpj = ClientCnpjHelper.normalize(value);
    if (cnpj.length !== 14) {
      return value || '';
    }
    return cnpj.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5');
  }

  /**
   * Consulta cada CNPJ e junta os resultados. Com um CNPJ só, é a consulta de sempre. Com CNPJs agrupados,
   * item sem CNPJ recebe o CNPJ consultado (para mostrar a origem) e erro num CNPJ agrupado só deixa os
   * itens dele de fora; erro no primeiro CNPJ (o do cliente) é propagado.
   */
  static async loadFromCnpjs<T extends { cnpj?: string | null }>(
    cnpjs: string[],
    loader: (cnpj: string) => Promise<T[]>,
  ): Promise<T[]> {
    if (cnpjs.length <= 1) {
      return loader(cnpjs[0]);
    }

    const results = await Promise.all(cnpjs.map(async (cnpj, index) => {
      try {
        const items = (await loader(cnpj)) || [];
        items.forEach((item) => {
          if (!item.cnpj) {
            // eslint-disable-next-line no-param-reassign
            item.cnpj = cnpj;
          }
        });
        return items;
      } catch (err) {
        if (index === 0) {
          throw err;
        }
        return [] as T[];
      }
    }));

    return results.reduce((all, items) => all.concat(items), [] as T[]);
  }
}
